import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getAuthLocalStorage } from '../../../storage/localStorage';
import { AuthApiService } from '../../../service/authApiService';
import { selectIsNightMode } from '../../../redux/slices/modeSlice';
import Logo from '../../../assets/images/common/comimport.png';
import LogoIcon from '../../../assets/images/common/importerr-favicon.png';
import {
  FaTachometerAlt, FaUsers, FaUserFriends, FaSearch, FaExchangeAlt,
  FaChartBar, FaListAlt, FaTag, FaStar, FaShoppingCart, FaGlobe,
  FaWarehouse, FaTicketAlt, FaBan, FaLayerGroup, FaBox, FaIdBadge,
  FaUpload, FaDollarSign, FaTruck, FaCheckCircle, FaEnvelopeOpenText,
  FaUserCog, FaStore, FaClock, FaUserShield, FaChevronRight, FaChevronLeft,
  FaChevronDown, FaTimes, FaSignOutAlt, FaCog, FaCoins, FaRobot, FaFileDownload,
  FaCogs, FaHandshake, FaShieldAlt, FaWallet
} from 'react-icons/fa';
import './adminSidebar.css';

// Menu key to link mapping for permission checking
const menuKeyToLinks = {
  'dashboard': ['/admin/dashboard'],
  'user-management': ['/admin/user-management'],
  'user-statistics': ['/admin/user-statistics'],
  'internal-users': ['/admin/internal-users'],
  'referral-management': ['/admin/referral-management'],
  'search-log': ['/admin/search-log'],
  'role-management': ['/admin/roles'],
  'products': ['/admin/products/list', '/admin/products/add', '/admin/product-errors', '/admin/product-export'],
  'product-drafts': ['/admin/product-drafts'],
  'product-errors': ['/admin/product-errors'],
  'categories': ['/admin/categories'],
  'featured-categories': ['/admin/featured-categories'],
  'category-approval': ['/admin/category-approval'],
  'tags': ['/admin/tags'],
  'global-product-search': ['/admin/alibaba-product'],
  'sku-upload': ['/admin/sku-upload'],
  'order-management': ['/admin/order-management'],
  'cross-border-orders': ['/admin/cross-border-orders'],
  'failed-orders-btpl': ['/admin/failed-orders'],
  'failed-orders-reseller': ['/admin/failed-orders'],
  'failed-orders-other': ['/admin/failed-orders'],
  'map-orders': ['/admin/map-orders'],
  'transactions': ['/admin/transactions'],
  'recharge-requests': ['/admin/recharge-requests'],
  'bank-transfer-settings': ['/admin/bank-transfer-settings'],
  'bank-transfer-leads': ['/admin/abandoned-leads/cart', '/admin/abandoned-leads/payment', '/admin/abandoned-leads/reminders'],
  'abandoned-leads-cart': ['/admin/abandoned-leads/cart'],
  'abandoned-leads-payment': ['/admin/abandoned-leads/payment'],
  'abandoned-leads-reminders': ['/admin/abandoned-leads/reminders'],
  'credit-management': ['/admin/credit-management'],
  'im-points': ['/admin/im-points/assign', '/admin/im-points/transactions'],
  'shipping-management': ['/admin/shipping-management'],
  'china-order-details': ['/admin/china-update/order-details'],
  'postage-to-india': ['/admin/china-update/postage-to-india'],
  'in-comments': ['/admin/china-update/in-comments'],
  'subscription': ['/admin/subscription/list', '/admin/subscription/add'],
  'membership-management': ['/admin/membership-management'],
  'pricing-algorithms': ['/admin/pricing-algorithms'],
  'warehouse-management': ['/admin/warehouse-management'],
  'logistics': ['/admin/logistics'],
  'supplier-data-info': ['/admin/supplier-data-info'],
  'ticket-management': ['/admin/ticket-management'],
  'inquiries': ['/admin/inquiries'],
  'canton-fair-leads': ['/admin/canton-fair-leads'],
  'supplier-inquiries': ['/admin/supplier-inquiries'],
  'cron-logs': ['/admin/cron-logs'],
  'cron-management': ['/admin/cron-management'],
  'ai-management': ['/admin/ai-management'],
  'reports': ['/admin/reports'],
  'affiliate-portal': ['/admin/affiliate-dashboard', '/admin/affiliate-management', '/admin/commission-plans', '/admin/payout-settings', '/admin/withdrawal-approvals', '/admin/fraud-monitor'],
};

// Organized menu structure with parent-child relationships
const adminMenuConfig = [
  {
    groupTitle: 'Main',
    items: [
      {
        name: 'Dashboard',
        icon: FaTachometerAlt,
        link: '/admin/dashboard',
      },
    ],
  },
  {
    groupTitle: 'Users & Analytics',
    items: [
      {
        name: 'Users',
        icon: FaUsers,
        children: [
          { name: 'User Management', link: '/admin/user-management' },
          { name: 'User Statistics', link: '/admin/user-statistics' },
        ],
      },
      {
        name: 'Referral Management',
        icon: FaUserFriends,
        link: '/admin/referral-management',
      },
      {
        name: 'Search Log',
        icon: FaSearch,
        link: '/admin/search-log',
      },
      {
        name: 'Role Management',
        icon: FaUserShield,
        link: '/admin/roles',
      },
    ],
  },
  {
    groupTitle: 'Products & Catalog',
    items: [
      {
        name: 'Products',
        icon: FaBox,
        children: [
          { name: 'Product List', link: '/admin/products/list' },
          // { name: 'Add Product', link: '/admin/products/add' }, // Commented for future use
          { name: 'Product Drafts', link: '/admin/product-drafts' },
          { name: 'Product Errors', link: '/admin/product-errors' },
          { name: 'Product Export', link: '/admin/product-export' },
        ],
      },
      {
        name: 'Categories',
        icon: FaListAlt,
        children: [
          { name: 'All Categories', link: '/admin/categories' },
          { name: 'Featured Categories', link: '/admin/featured-categories' },
          { name: 'Category Approval', link: '/admin/category-approval' },
        ],
      },
      {
        name: 'Tags',
        icon: FaTag,
        link: '/admin/tags',
      },
      {
        name: 'Global Product Search',
        icon: FaSearch,
        link: '/admin/alibaba-product',
      },
      {
        name: 'Internal Users',
        icon: FaUserCog,
        link: '/admin/internal-users',
      },
      {
        name: 'SKU Upload',
        icon: FaUpload,
        link: '/admin/sku-upload',
      },
      {
        name: 'Restricted Words',
        icon: FaBan,
        link: '/admin/restricted-words'
      }
    ],
  },
  {
    groupTitle: 'Orders & Transactions',
    items: [
      {
        name: 'Orders',
        icon: FaShoppingCart,
        children: [
          { name: 'Order Management', link: '/admin/order-management' },
          { name: 'Cross-Border Orders', link: '/admin/cross-border-orders' },
          { name: 'Failed Orders', link: '/admin/failed-orders' },
          { name: 'Map Orders', link: '/admin/map-orders' },
        ],
      },
      {
        name: 'Transactions',
        icon: FaExchangeAlt,
        link: '/admin/transactions',
      },
      {
        name: 'Recharge Requests',
        icon: FaCoins,
        link: '/admin/recharge-requests',
      },
      {
        name: 'Abandoned Leads',
        icon: FaEnvelopeOpenText,
        children: [
          { name: 'Abandoned Cart', link: '/admin/abandoned-leads/cart' },
          { name: 'Abandoned Payment', link: '/admin/abandoned-leads/payment' },
          { name: 'Reminder emails', link: '/admin/abandoned-leads/reminders' },
        ],
      },
      {
        name: 'Bank Transfer',
        icon: FaWallet,
        link: '/admin/bank-transfer-settings',
        // children: [
        //   { name: 'Bank Details Settings', link: '/admin/bank-transfer-settings' },
        // ],
      },
      {
        name: 'Credit Amount',
        icon: FaCoins,
        link: '/admin/credit-management',
      },
      {
        name: 'IM Points',
        icon: FaCoins,
        children: [
          { name: 'Assign Points', link: '/admin/im-points/assign' },
          { name: 'Transaction History', link: '/admin/im-points/transactions' },
        ],
      },
      {
        name: 'Shipping Management',
        icon: FaTruck,
        link: '/admin/shipping-management',
      },
      {
        name: 'China Update',
        icon: FaGlobe,
        children: [
          { name: 'Order Details', link: '/admin/china-update/order-details' },
          { name: 'Postage to India', link: '/admin/china-update/postage-to-india' },
          { name: 'IN Comments', link: '/admin/china-update/in-comments' },
        ],
      },
    ],
  },
  {
    groupTitle: 'Subscriptions & Pricing',
    items: [
      {
        name: 'Subscription',
        icon: FaLayerGroup,
        children: [
          { name: 'Subscription List', link: '/admin/subscription/list' },
          { name: 'Add Subscription', link: '/admin/subscription/add' },
        ],
      },
      {
        name: 'Membership Manage',
        icon: FaIdBadge,
        link: '/admin/membership-management',
      },
      {
        name: 'Pricing Algorithms',
        icon: FaDollarSign,
        link: '/admin/pricing-algorithms',
      },
    ],
  },
  {
    groupTitle: 'Operations',
    items: [
      {
        name: 'Warehouse Management',
        icon: FaWarehouse,
        link: '/admin/warehouse-management',
      },
      {
        name: 'Logistics',
        icon: FaTruck,
        link: '/admin/logistics',
      },
      {
        name: 'Supplier Data Info',
        icon: FaStore,
        link: '/admin/supplier-data-info',
      },
    ],
  },
  {
    groupTitle: 'Support & System',
    items: [
      {
        name: 'Support',
        icon: FaTicketAlt,
        children: [
          { name: 'Ticket Management', link: '/admin/ticket-management' },
          { name: 'Inquiries', link: '/admin/inquiries' },
          { name: 'Exporterr supplier inquiries', link: '/admin/supplier-inquiries' },
          { name: 'Canton Fair leads', link: '/admin/canton-fair-leads' },
        ],
      },
      {
        name: 'Cron Logs',
        icon: FaClock,
        link: '/admin/cron-logs',
      },
      {
        name: 'Cron Management',
        icon: FaCogs,
        link: '/admin/cron-management',
      },
      {
        name: 'AI Management',
        icon: FaRobot,
        link: '/admin/ai-management',
      },
      {
        name: 'Reports & Downloads',
        icon: FaFileDownload,
        link: '/admin/reports',
      },
    ],
  },
    {
    groupTitle: 'Affiliate Portal',
    items: [
      {
        name: 'Affiliate Portal',
        icon: FaHandshake,
        children: [
          { name: 'Dashboard', link: '/admin/affiliate-dashboard' },
          { name: 'Affiliates', link: '/admin/affiliate-management' },
          { name: 'Commission Plans', link: '/admin/commission-plans' },
          { name: 'Payout Setting', link: '/admin/payout-settings' },
          { name: 'Withdrawals', link: '/admin/withdrawal-approvals' },
          { name: 'Fraud Monitor', link: '/admin/fraud-monitor' },
        ],
      },
    ],
  },
];

export default function AdminSidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState({});
  const [userData, setUserData] = useState(null);
  const [assignedMenus, setAssignedMenus] = useState([]);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const auth = getAuthLocalStorage();
    if (auth) {
      const role = auth.userType || localStorage.getItem('userType') || 'admin';
      setUserRole(role.toLowerCase());
      
      // Initially load from localStorage for quick render
      try {
        const storedMenus = localStorage.getItem('assignedMenus');
        if (storedMenus) {
          setAssignedMenus(JSON.parse(storedMenus));
        }
      } catch (e) {
        console.error('Error parsing assignedMenus:', e);
      }

      // Fetch user profile from API - this ensures already logged-in users get latest permissions
      AuthApiService.getProfile()
        .then((res) => {
          if (res?.status?.code === 200 && res?.data) {
            const firstName = res.data.firstName || '';
            const lastName = res.data.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim() || 'Admin User';
            const userRole = res.data.role || role;
            
            setUserData({
              name: fullName,
              role: userRole,
            });
            setUserRole(userRole.toLowerCase());
            
            // Update assignedMenus from API response and sync to localStorage
            if (res.data.assignedMenus && Array.isArray(res.data.assignedMenus)) {
              setAssignedMenus(res.data.assignedMenus);
              localStorage.setItem('assignedMenus', JSON.stringify(res.data.assignedMenus));
            }
            
            // Also update userType in localStorage if different
            if (res.data.role) {
              localStorage.setItem('userType', res.data.role);
            }
          } else {
            setUserData({
              name: 'Admin User',
              role: role,
            });
          }
        })
        .catch(() => {
          setUserData({
            name: 'Admin User',
            role: role,
          });
        });
    }
  }, []);

  // Check if a menu link is allowed based on permissions
  const isMenuAllowed = (link) => {
    // Admin has full access
    if (userRole === 'admin') return true;
    
    // For sub-admin, check assigned menus
    if (userRole === 'sub-admin' && assignedMenus.length > 0) {
      // Check if any assigned menu has canView and matches the link
      return assignedMenus.some(menu => {
        if (!menu.canView) return false;
        // Check by menuLink
        if (menu.menuLink === link) return true;
        // Check by menuKey using the mapping
        const menuLinks = menuKeyToLinks[menu.menuKey] || [];
        return menuLinks.includes(link);
      });
    }
    
    // If no assigned menus for sub-admin, show nothing
    if (userRole === 'sub-admin') return false;
    
    // Default allow for other roles
    return true;
  };

  // Filter menu config based on permissions
  const filteredMenuConfig = useMemo(() => {
    if (userRole === 'admin') return adminMenuConfig;
    
    return adminMenuConfig.map(group => {
      const filteredItems = group.items.filter(item => {
        if (item.children) {
          // For parent items, check if any child is allowed
          const allowedChildren = item.children.filter(child => isMenuAllowed(child.link));
          return allowedChildren.length > 0;
        } else {
          return isMenuAllowed(item.link);
        }
      }).map(item => {
        if (item.children) {
          // Filter children based on permissions
          return {
            ...item,
            children: item.children.filter(child => isMenuAllowed(child.link))
          };
        }
        return item;
      });

      return { ...group, items: filteredItems };
    }).filter(group => group.items.length > 0);
  }, [userRole, assignedMenus]);


  // Auto-expand parent menu if child is active
  useEffect(() => {
    filteredMenuConfig.forEach(group => {
      group.items.forEach(item => {
        if (item.children) {
          const isChildActive = item.children.some(child => 
            location.pathname === child.link || location.pathname.startsWith(child.link)
          );
          if (isChildActive) {
            setOpenMenus(prev => ({ ...prev, [item.name]: true }));
          }
        }
      });
    });
  }, [location.pathname, filteredMenuConfig]);

  const toggleMenu = (menuName) => {
    setOpenMenus(prev => {
      // If clicking the same menu, toggle it
      if (prev[menuName]) {
        return { ...prev, [menuName]: false };
      }
      // Otherwise, close all other menus and open the clicked one
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      newState[menuName] = true;
      return newState;
    });
  };

  const isActive = (link) => {
    return location.pathname === link || location.pathname.startsWith(link + '/');
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
  };

  const getInitials = (name) => {
    if (!name) return 'A';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <>
      <div className={`kiro-sidebar-overlay ${mobileOpen ? 'active' : ''}`} onClick={closeMobileMenu} />
      
      <aside className={`kiro-admin-sidebar ${collapsed ? 'kiro-sidebar-collapsed' : ''} ${mobileOpen ? 'kiro-sidebar-mobile-open' : ''}`}>
        {/* Mobile Close Button */}
        <button className="kiro-sidebar-mobile-close" onClick={closeMobileMenu}>
          <FaTimes />
        </button>

        {/* Logo Section */}
        <div className="kiro-sidebar-logo-section">
          <Link to="/" className="kiro-sidebar-logo-wrapper">
            <img 
              src={collapsed ? LogoIcon : Logo} 
              alt="Logo" 
              className="kiro-sidebar-logo-img" 
            />
            {/* {!collapsed && <span className="kiro-sidebar-logo-text">importerr</span>} */}
          </Link>
          <button 
            className="kiro-sidebar-toggle-btn" 
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </div>

        {/* Menu Container */}
        <nav className="kiro-sidebar-menu-container">
          {filteredMenuConfig.map((group, groupIndex) => (
            <div key={groupIndex} className="kiro-menu-group">
              <div className="kiro-menu-group-title">{group.groupTitle}</div>
              
              {group.items.map((item, itemIndex) => (
                <div key={itemIndex} className="kiro-menu-item">
                  {item.children ? (
                    // Parent menu with children
                    <>
                      <button
                        className={`kiro-menu-item-btn kiro-has-children ${item.children.some(c => isActive(c.link)) ? 'kiro-menu-active' : ''} ${openMenus[item.name] ? 'kiro-menu-expanded' : ''}`}
                        onClick={() => toggleMenu(item.name)}
                      >
                        <span className="kiro-menu-item-icon">
                          <item.icon />
                        </span>
                        <span className="kiro-menu-item-text">{item.name}</span>
                        <FaChevronDown className={`kiro-menu-item-arrow ${openMenus[item.name] ? 'kiro-arrow-open' : ''}`} />
                        {collapsed && <span className="kiro-menu-tooltip">{item.name}</span>}
                      </button>
                      
                      <div className={`kiro-submenu ${openMenus[item.name] ? 'kiro-submenu-open' : ''}`}>
                        {item.children.map((child, childIndex) => (
                          <Link
                            key={childIndex}
                            to={child.link}
                            className={`kiro-submenu-item ${isActive(child.link) ? 'kiro-submenu-active' : ''}`}
                            onClick={closeMobileMenu}
                          >
                            <span className="kiro-submenu-dot" />
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    // Single menu item
                    <Link
                      to={item.link}
                      className={`kiro-menu-item-btn ${isActive(item.link) ? 'kiro-menu-active' : ''}`}
                      onClick={closeMobileMenu}
                    >
                      <span className="kiro-menu-item-icon">
                        <item.icon />
                      </span>
                      <span className="kiro-menu-item-text">{item.name}</span>
                      {collapsed && <span className="kiro-menu-tooltip">{item.name}</span>}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          ))}
        </nav>

        {/* User Section */}
        <div className="kiro-sidebar-user-section">
          <div className="kiro-sidebar-user-card" onClick={handleLogout}>
            <div className="kiro-sidebar-user-avatar">
              {getInitials(userData?.name)}
            </div>
            <div className="kiro-sidebar-user-info">
              <div className="kiro-sidebar-user-name">{userData?.name}</div>
              <div className="kiro-sidebar-user-role">{userData?.role}</div>
            </div>
            <FaSignOutAlt style={{ color: 'var(--kiro-sidebar-text-muted)' }} />
          </div>
        </div>
      </aside>
    </>
  );
}
